import { Fragment, useMemo, useState } from "react";
import { money, anyDay as day } from "../api.js";
import { useResource } from "./useResource.js";
import { Empty, shortName } from "./common.jsx";
import { DownloadButton } from "./Reports.jsx";
import { filterSortMovement, TEXT_SORT, DEFAULT_SORT } from "../../../lib/movement-view.js";

// Рух товарів і послуг по кодах УКТЗЕД / ДКПП — як «Рух по УКТЗЕД» дашборда etax
const q3 = (v) => (v == null ? "" : Number(v).toLocaleString("uk-UA", { maximumFractionDigits: 3 }));
// Кількість з одиницею; кілька одиниць одного коду — не одне число, а «різні од.» з розкладом у підказці
const unitText = (u) => u || "од. не вказана";
function Qty({ x, f }) {
  if (!x.mixedUnits) return <>{q3(x[f])}{x.unit && x[f] ? <span className="muted small"> {x.unit}</span> : null}</>;
  const parts = x.units.filter((u) => u[f]).map((u) => `${q3(u[f])} ${unitText(u.unit)}`);
  return parts.length ? <span className="muted small mixed-qty" title={parts.join("; ")}>різні од.: {parts.join("; ")}</span> : <>0</>;
}
const KINDS = [["all", "Усі"], ["uktzed", "Товари"], ["dkpp", "Послуги"]];
// Сортування: за замовчуванням — оборот (як було). Заголовок колонки: число — спершу від більшого до меншого,
// код — за зростанням; повторне натискання — навпаки. Правила — lib/movement-view.js (ті самі, що в Excel)
function SortTh({ k, sort, setSort, children, className = "num", rowSpan }) {
  const on = sort.key === k, first = TEXT_SORT.has(k) ? "asc" : "desc";
  const next = () => setSort(on ? { key: k, dir: sort.dir === "desc" ? "asc" : "desc" } : { key: k, dir: first });
  const hint = (on ? (sort.dir === "desc" ? "asc" : "desc") : first) === "desc" ? "від більшого до меншого" : TEXT_SORT.has(k) ? "за зростанням коду" : "від меншого до більшого";
  return (
    <th className={className} rowSpan={rowSpan} aria-sort={on ? (sort.dir === "desc" ? "descending" : "ascending") : "none"}>
      <button type="button" className="th-sort" onClick={next} title={`Сортувати ${hint}`}>
        {children}<span aria-hidden="true">{on ? (sort.dir === "desc" ? " ↓" : " ↑") : " ↕"}</span></button>
    </th>
  );
}
const OP = { purchase: ["Придбання", "badge-idle"], sale: ["Реалізація", "badge-ok"], import: ["Імпорт (МД)", "badge-warn"] };

// Посилання на створення ПН з вибраними кодами (PnNew читає їх із hash)
// Вибране: коди (рядок ПН на код) і окремі позиції операцій (рядок на позицію; ключі — server/src/movement.js MOVE_KEY)
export const pnFromMoveHref = (companyId, keys) => `#/c/${companyId}/pn?items=${keys.map(encodeURIComponent).join(",")}`;
const PN_MAX = 50;
export const pickKey = (companyId) => `etax.movementPick.${companyId}`;
// Вибір зі сховища вкладки: [[ключ, підпис], …] → Map
export const readPicked = (companyId) => {
  try { return new Map(JSON.parse(sessionStorage.getItem(pickKey(companyId)) || "[]").map((x) => (Array.isArray(x) ? x : [x, x]))); } catch { return new Map(); }
};
const codeLabel = (x) => `${x.code} · ${x.name || ""}`;
const posLabel = (x) => `${x.name || x.code} · ${q3(x.qty)} ${x.unit || ""} · ${x.doc}`;

export default function Movement({ companyId, company, versions }) {
  const canPn = ["preparer", "submitter", "owner"].includes(company?.role);
  // Вибір переживає перехід до ПН і повернення «Змінити вибір» (лише в цій вкладці браузера)
  const [picked, setPickedRaw] = useState(() => readPicked(companyId));
  const setPicked = (f) => setPickedRaw((p) => {
    const n = typeof f === "function" ? f(p) : f;
    try { sessionStorage.setItem(pickKey(companyId), JSON.stringify([...n])); } catch {}
    return n;
  });
  const toggle = (key, label) => setPicked((p) => { const n = new Map(p); if (n.has(key)) n.delete(key); else n.set(key, label); return n; });
  const r = useResource(`/api/companies/${companyId}/movement`, `${versions.erpn || 0}:${versions.sea || 0}:${versions.customs || 0}`);
  const [q, setQ] = useState("");
  const [nonzero, setNonzero] = useState(false);
  const [risk, setRisk] = useState("all");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => filterSortMovement(r.data?.rows || [], { q, kind, risk, nonzero, sort }), [r.data, q, nonzero, risk, kind, sort]);
  // Excel — той самий зріз: фільтри й порядок передаємо серверу
  const xlsx = `/api/companies/${companyId}/movement-export?${new URLSearchParams({ q, kind, risk, nonzero: nonzero ? "1" : "", sort: sort.key, dir: sort.dir })}`;
  const tot = rows.reduce((a, x) => ({ inV: a.inV + x.inValue, outV: a.outV + x.outValue, bal: a.bal + x.balance, vat: a.vat + x.balanceVat, unk: a.unk + x.vatUnknown }), { inV: 0, outV: 0, bal: 0, vat: 0, unk: 0 });
  const d = r.data;
  // «Вибрати всі» — у видимих (відфільтрованих) рядках; коди без значення (рядки без УКТЗЕД) не вибираються
  const pickable = rows.filter((x) => x.code);
  const allOn = pickable.length > 0 && pickable.every((x) => picked.has(x.code));
  const toggleAll = () => setPicked((p) => { const n = new Map(p); for (const x of pickable) { if (allOn) n.delete(x.code); else n.set(x.code, codeLabel(x)); } return n; });
  const sel = [...picked.keys()];
  return (
    <>
      <div className="section-head">
        <div>
          <h2>Рух товарів і послуг</h2>
          <p className="notice">По кодах УКТЗЕД і ДКПП з позицій ПН/РК{d?.seaFiltered ? ", відображених у СЕА" : ""}, та імпорту за МД. Це рух за податковими документами,
            а не фізичний склад. Кількості — окремо для кожної одиниці. Суми без ПДВ; залишок з ПДВ — за ставками документів: ПН — 20, 7 чи 14%,
            0% і звільнені — без ПДВ; МД — фактичний ПДВ.</p>
        </div>
      </div>
      <div className="card">
        <div className="row toolbar">
          <input className="search" type="search" aria-label="Пошук за кодом або назвою" placeholder="Код або назва" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="check"><input type="checkbox" checked={nonzero} onChange={(e) => setNonzero(e.target.checked)} /> Лише з ненульовим залишком</label>
          <div className="tabs" role="group" aria-label="Товари чи послуги">
            {KINDS.map(([k, t]) => (
              <button key={k} type="button" className={kind === k ? "tab active" : "tab"} aria-pressed={kind === k} onClick={() => setKind(k)}>{t}</button>
            ))}
          </div>
          <div className="tabs" role="group" aria-label="Перелік ДПС №67">
            {[["all", "Усі"], ["in", "У переліку ДПС"], ["out", "Поза переліком"]].map(([k, t]) => (
              <button key={k} type="button" className={risk === k ? "tab active" : "tab"} aria-pressed={risk === k} onClick={() => setRisk(k)}>{t}</button>
            ))}
          </div>
          <span className="muted small">Позицій: {rows.length}</span>
          {rows.length > 0 && <DownloadButton path={xlsx}>Експорт у Excel</DownloadButton>}
        </div>
        {canPn && sel.length > 0 && (
          <div className="row toolbar movement-pick" role="region" aria-label="Вибрані позиції">
            <span><b>Вибрано: {sel.length}</b> <span className="muted small">коди й окремі позиції — далі виберете кількість із залишку й націнку</span>{sel.length > PN_MAX && <span className="error small"> — в одну ПН не більше {PN_MAX} позицій</span>}</span>
            {sel.length <= PN_MAX
              ? <a className="btn btn-sm" href={pnFromMoveHref(companyId, sel)}>Сформувати ПН →</a>
              : <button type="button" className="btn btn-sm" disabled>Сформувати ПН →</button>}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPicked(new Set())}>Зняти вибір</button>
          </div>
        )}
        {tot.unk > 0 && <p className="notice">Рядків ПН без відомої ставки ПДВ: {tot.unk} — у сумі з ПДВ їх враховано без ПДВ.</p>}
        {d?.customs?.n > 0 && d.customs.in_sea < d.customs.n && (
          <p className="notice">МД у русі: {d.customs.in_sea} з {d.customs.n} — решта ще не відображена в реєстрі СЕА{d.seaFiltered ? "" : " (синхронізуйте СЕА)"}.</p>
        )}
        {r.pending ? <p className="muted" role="status">Завантаження…</p>
          : r.error && !d ? <p className="error">Не вдалося завантажити: {r.error.message}</p>
          : !d.rows.length ? <Empty>Руху ще немає — позиції з'являться після синку ЄРПН або завантаження МД.</Empty>
          : !rows.length ? <Empty>Нічого не знайдено.</Empty> : (
            <div className="table-wrap">
              <table className={`table-static movement${canPn ? " pickable" : ""}`}>
                <thead>
                  <tr>{canPn && <th rowSpan={2} className="pick-cell"><input type="checkbox" aria-label="Вибрати всі видимі позиції" checked={allOn} onChange={toggleAll} disabled={!pickable.length} /></th>}<SortTh k="code" sort={sort} setSort={setSort} className="code-cell" rowSpan={2}>Код і назва</SortTh><th colSpan={2} className="center-cell">Надходження</th><th colSpan={2} className="center-cell">Вибуття</th><th colSpan={3} className="center-cell">Залишок</th></tr>
                  <tr>
                    <SortTh k="inflowQty" sort={sort} setSort={setSort}>К-сть</SortTh><SortTh k="inValue" sort={sort} setSort={setSort}>Сума</SortTh>
                    <SortTh k="qtyOut" sort={sort} setSort={setSort}>К-сть</SortTh><SortTh k="outValue" sort={sort} setSort={setSort}>Сума</SortTh>
                    <SortTh k="balanceQty" sort={sort} setSort={setSort}>К-сть</SortTh>
                    <SortTh k="balance" sort={sort} setSort={setSort}>Без ПДВ</SortTh><SortTh k="balanceVat" sort={sort} setSort={setSort}>З ПДВ</SortTh></tr>
                </thead>
                <tbody>
                  {rows.map((x) => (
                    <Fragment key={x.code}>
                      <tr className={open === x.code ? "selected clickable" : "clickable"} tabIndex={0} aria-expanded={open === x.code}
                        onClick={() => setOpen(open === x.code ? null : x.code)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(open === x.code ? null : x.code); } }}>
                        {canPn && <td className="pick-cell" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                          {x.code && <input type="checkbox" aria-label={`Вибрати ${x.code} для ПН`} checked={picked.has(x.code)} onChange={() => toggle(x.code, codeLabel(x))} />}</td>}
                        <td className="wrap-cell code-cell">
                          <a href={`#/c/${companyId}/reference/${x.kind}/${encodeURIComponent(x.code)}`} onClick={(e) => e.stopPropagation()} className="mono-sm"><b>{x.code}</b></a>
                          {x.risky && <span className="badge badge-warn" title="Група в переліку для моніторингу (наказ ДПС № 67)">перелік ДПС № 67</span>}
                          <br /><span className="muted" title={x.fullName}>{x.name}</span>
                        </td>
                        <td className="num"><Qty x={x} f="inflowQty" /></td><td className="num">{money(x.inValue)}</td>
                        <td className="num"><Qty x={x} f="qtyOut" /></td><td className="num">{money(x.outValue)}</td>
                        <td className="num"><Qty x={x} f="balanceQty" /></td><td className="num">{money(x.balance)}</td>
                        <td className="num"><b>{money(x.balanceVat)}</b>{x.vatUnknown > 0 && <span className="muted small" title="Є рядки ПН без відомої ставки ПДВ — їх враховано без ПДВ"> *</span>}</td>
                      </tr>
                      {open === x.code && <tr className="details-row"><td colSpan={canPn ? 9 : 8}><MoveDetails companyId={companyId} code={x.code} pick={canPn ? { picked, toggle } : null} /></td></tr>}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot><tr>{canPn && <th className="pick-cell" />}<th className="code-cell">Разом</th><th /><th className="num">{money(tot.inV)}</th><th /><th className="num">{money(tot.outV)}</th><th /><th className="num">{money(tot.bal)}</th><th className="num">{money(tot.vat)}</th></tr></tfoot>
              </table>
            </div>
          )}
      </div>
    </>
  );
}

// Операції коду; з галочками — вибір окремих позицій (назва й кількість саме цього рядка ПН чи позиції МД)
function MoveDetails({ companyId, code, pick }) {
  const r = useResource(`/api/companies/${companyId}/movement/${encodeURIComponent(code)}`);
  const [all, setAll] = useState(false);
  if (r.pending) return <p className="muted" role="status">Завантаження…</p>;
  if (r.error) return <p className="error">Не вдалося завантажити: {r.error.message}</p>;
  const k = r.data.smkor;
  return (
    <div className="table-wrap">
      {k && (
        <p className={`move-smkor small${k.available < 0 ? " bad" : ""}`}>
          <b>Критерій 1 СМКОР, {k.label.replace(/^Група/, "група").replace(/^Послуга/, "послуга")}:</b> доступно <b>{money(k.available)} грн</b>
          {" "}= (придбано з ПДВ {money(k.bought)}{k.imported ? ` + ввезено ${money(k.imported)}` : ""}) × {String(k.mult).replace(".", ",")} − продано {money(k.sold)}.
          <span className="muted"> Це не залишок «Руху»: ДПС рахує всю групу{k.kind === "goods" ? " за 4 цифрами коду" : ""}, лише зареєстровані ПН/РК, придбання — зі ставкою 20, 14 чи 7% з 2017 р., і множить на {String(k.mult).replace(".", ",")}.
            {k.available < 0 ? " ПН з цією групою ризикує бути зупиненою, якщо не діє безумовна реєстрація чи позитивна податкова історія." : ""}</span>
        </p>
      )}
      <table className="table-static mini">
        <thead><tr>{pick && <th className="pick-cell"><span className="sr-only">Вибрати для ПН</span></th>}<th>Операція</th><th>Найменування</th><th className="num">К-сть</th><th className="num">Сума без ПДВ</th><th>Документ</th><th>Контрагент / країна</th><th>Дата</th></tr></thead>
        <tbody>
          {(all ? r.data.details : r.data.details.slice(0, 50)).map((x, i) => {
            const [label, cls] = OP[x.op];
            return (
              <tr key={x.key || i} className={pick?.picked.has(x.key) ? "picked" : undefined}>
                {pick && <td className="pick-cell">{x.key && <input type="checkbox" aria-label={`Вибрати для ПН: ${x.name || x.code}, ${x.doc}`}
                  checked={pick.picked.has(x.key)} onChange={() => pick.toggle(x.key, posLabel(x))} />}</td>}
                <td><span className={`badge ${cls}`}>{label}</span></td>
                <td className="wrap-cell">{x.name}</td>
                <td className="num">{q3(x.qty)} {x.unit || ""}</td>
                <td className="num">{money(x.value)}</td>
                <td>{x.mrn ? <a href={`#/c/${companyId}/customs/${encodeURIComponent(x.mrn)}`}>{x.doc}</a> : x.doc}</td>
                <td className="wrap-cell">{x.partyTin ? <a href={`#/c/${companyId}/partners/${x.partyTin}`}>{shortName(x.party)}</a> : x.party}</td>
                <td>{day(x.dt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {r.data.details.length > 50 && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAll((v) => !v)} aria-expanded={all}>
          {all ? "Згорнути" : `Показати всі ${r.data.details.length}`}
        </button>
      )}
    </div>
  );
}
